import { useState, useEffect, useRef, useMemo } from 'react';
import './CopytradeTab.css';
import { useBotWallet } from './useBotWallet';
import { botEngine, getBotStats } from './bot/botEngine';
import { getBotLogs } from './bot/botLogger';
import { buyToken, sellTokenPercent } from './bot/jupiterSwap';
import { supabase } from './supabase';

const STORAGE_KEY = 'copytrade-configs';
const API_KEY = localStorage.getItem('helius-api-key') || process.env.REACT_APP_HELIUS_API_KEY || '';
const HELIUS = 'https://api.helius.xyz/v0';

const INITIAL_CONFIGS = [];
let nextConfigId = 6;

function loadConfigs() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (!s) return INITIAL_CONFIGS;
    const configs = JSON.parse(s);
    return configs.map(c => { const { mode, autoSell, ...rest } = c; return rest; });
  } catch { return INITIAL_CONFIGS; }
}

async function loadConfigsFromSupabase() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const { data, error } = await supabase.from('configs').select('*').eq('user_id', session.user.id).order('created_at', { ascending: true });
    if (error || !data || data.length === 0) return null;
    return data.map((c, i) => ({
      id: c.id || (nextConfigId + i),
      name: c.name,
      emoji: c.emoji || '',
      targetWallet: c.target_wallet,
      botWalletId: c.bot_wallet_id,
      amountMode: c.amount_mode,
      amountValue: c.amount_value,
      slippage: c.slippage,
      jitoTip: c.jito_tip,
      copySells: c.copy_sells,
      stopLoss: c.stop_loss,
      takeProfit: c.take_profit,
      stopLossEnabled: c.stop_loss_enabled,
      takeProfitEnabled: c.take_profit_enabled,
      trailingStopLoss: c.trailing_stop_loss,
      trailingStopLossEnabled: c.trailing_stop_loss_enabled,
      securityFilterEnabled: c.security_filter_enabled,
      enabled: c.enabled,
      type: 'manual',
      status: c.enabled,
    }));
  } catch(e) {
    console.warn('Erreur chargement configs Supabase:', e.message);
    return null;
  }
}

function saveConfigs(c) {
  try { localStorage.setItem(STORAGE_KEY,JSON.stringify(c)); } catch {}
  // Sauvegarder dans Supabase — delete puis insert séquentiels pour éviter la perte de données
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const userId = session.user.id;
      const { error: delErr } = await supabase.from('configs').delete().eq('user_id', userId);
      if (delErr) { console.warn('Config delete error:', delErr); return; }
      if (c.length === 0) return;
      const rows = c.map(cfg => ({
        user_id: userId,
        name: cfg.name || 'Config',
        emoji: cfg.emoji || '',
        target_wallet: cfg.targetWallet || '',
        bot_wallet_id: cfg.botWalletId || '',
        amount_mode: cfg.amountMode || 'fixed',
        amount_value: String(cfg.amountValue || '0.1'),
        slippage: cfg.slippage || 'auto',
        jito_tip: cfg.jitoTip || 'low',
        copy_sells: cfg.copySells || false,
        stop_loss: String(cfg.stopLoss || '50'),
        take_profit: String(cfg.takeProfit || '200'),
        stop_loss_enabled: cfg.stopLossEnabled !== false,
        take_profit_enabled: cfg.takeProfitEnabled !== false,
        trailing_stop_loss: String(cfg.trailingStopLoss || '20'),
        trailing_stop_loss_enabled: cfg.trailingStopLossEnabled === true,
        security_filter_enabled: cfg.securityFilterEnabled !== false,
        enabled: cfg.enabled || false,
      }));
      const { error: insErr } = await supabase.from('configs').insert(rows);
      if (insErr) console.warn('Config insert error:', insErr);
    } catch(e) { console.warn('Config save error:', e.message); }
  })();
}

function timeAgo(ts) {
  const diff = Math.floor(Date.now()/1000) - ts;
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff/60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff/3600)}h`;
  return `il y a ${Math.floor(diff/86400)}j`;
}

function duration(startTs, endTs) {
  const diff = (endTs || Math.floor(Date.now()/1000)) - startTs;
  if (diff < 3600) return `${Math.floor(diff/60)}min`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h${Math.floor((diff%3600)/60)}min`;
  return `${Math.floor(diff/86400)}j`;
}

async function fetchSolPrice() {
  try {
    const r = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT');
    const j = await r.json();
    return parseFloat(j.price) || 150;
  } catch { return 150; }
}

async function fetchWalletTrades(address, limit=15) {
  try {
    const r = await fetch(`${HELIUS}/addresses/${address}/transactions?api-key=${API_KEY}&limit=${limit}&type=SWAP`);
    if(!r.ok) return [];
    const txns = await r.json();
    return txns.map(tx => {
      const buy  = (tx.tokenTransfers||[]).find(t=>t.toUserAccount===address&&parseFloat(t.tokenAmount)>0);
      const sell = (tx.tokenTransfers||[]).find(t=>t.fromUserAccount===address&&parseFloat(t.tokenAmount)>0);
      const sol  = (tx.nativeTransfers||[]).reduce((s,t)=>{
        if(t.fromUserAccount===address) return s-t.amount;
        if(t.toUserAccount===address)   return s+t.amount;
        return s;
      },0);
      const token = buy||sell;
      if(!token) return null;
      return {
        signature: tx.signature, timestamp: tx.timestamp,
        type: buy?'BUY':'SELL', mint: token.mint,
        symbol: token.tokenSymbol||token.mint?.slice(0,6)||'?',
        tokens: parseFloat(token.tokenAmount||0), solDelta: Math.abs(sol)/1e9,
      };
    }).filter(Boolean);
  } catch { return []; }
}

async function fetchLastBoughtMint(address) {
  try { const trades=await fetchWalletTrades(address,10); return trades.find(t=>t.type==='BUY')?.mint||null; }
  catch { return null; }
}

async function fetchTokenInfo(mint) {
  try {
    const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`);
    const j = await r.json();
    const p = j?.pairs?.[0];
    if(!p) return null;
    return {
      mint, pairAddress:p.pairAddress,
      symbol:p.baseToken?.symbol||mint.slice(0,6), name:p.baseToken?.name||mint.slice(0,8),
      priceUsd:parseFloat(p.priceUsd||0), mcap:p.fdv||0, liq:p.liquidity?.usd||0,
      vol24h:p.volume?.h24||0, change24h:p.priceChange?.h24||0,
      dexUrl:p.url||`https://dexscreener.com/solana/${mint}`,
    };
  } catch { return null; }
}

function fmt(n) {
  if(!n||isNaN(n)) return '—';
  if(n>=1e6) return (n/1e6).toFixed(1)+'M';
  if(n>=1e3) return (n/1e3).toFixed(1)+'K';
  return n.toFixed(2);
}

function calcAnalytics(trades) {
  const closed = trades.filter(t=>t.resultat&&t.resultat!=='En cours');
  if(!closed.length) return null;
  const winners = closed.filter(t=>(t.pnlUsd||0)>=0);
  const losers = closed.filter(t=>(t.pnlUsd||0)<0);
  const totalPnl = closed.reduce((s,t)=>s+(t.pnlUsd||0),0);
  const totalInvested = closed.reduce((s,t)=>s+(t.achatUsd||0),0);
  const bestTrade = closed.reduce((best,t)=>(t.roi||0)>(best.roi||0)?t:best, closed[0]);
  const worstTrade = closed.reduce((worst,t)=>(t.roi||0)<(worst.roi||0)?t:worst, closed[0]);
  const avgDuration = closed.filter(t=>t.entreeTs&&t.sortieTs).reduce((s,t)=>s+(t.sortieTs-t.entreeTs),0) / (closed.filter(t=>t.entreeTs&&t.sortieTs).length||1);
  const simTrades = closed.filter(t=>t.isSim);
  const liveTrades = closed.filter(t=>!t.isSim);
  return {
    total: closed.length, winners: winners.length, losers: losers.length,
    winRate: Math.round((winners.length/closed.length)*100),
    totalPnl, totalInvested,
    totalPnlPct: totalInvested>0?(totalPnl/totalInvested)*100:0,
    bestTrade, worstTrade, avgDuration,
    simCount: simTrades.length, liveCount: liveTrades.length,
    simPnl: simTrades.reduce((s,t)=>s+(t.pnlUsd||0),0),
    livePnl: liveTrades.reduce((s,t)=>s+(t.pnlUsd||0),0),
  };
}

function useManualTrade(keypair, vpsWalletId = null) {
  const [buyLoading,setBuyLoading]=useState(false);
  const [sellLoading,setSellLoading]=useState(false);
  const [msg,setMsg]=useState(null);
  const showMsg=(text,type='ok')=>{ setMsg({text,type}); setTimeout(()=>setMsg(null),6000); };

  const executeBuy=async(tokenMint,amountSol,slippage='auto',jitoTip='low')=>{
    if(!keypair && !vpsWalletId && keypair !== 'sim') return showMsg('Wallet bot non accessible','err');
    if(!tokenMint) return showMsg('Aucun token sélectionné','err');
    const amt=parseFloat(amountSol);
    if(!amt||amt<=0) return showMsg('Montant invalide','err');
    setBuyLoading(true);
    try {
      if(keypair === 'sim') {
        const result = await botEngine.executSimBuy(tokenMint, amt);
        showMsg(`Sim achat ${amt} SOL — ${result.tokenAmount.toFixed(0)} tokens`);
      } else if(vpsWalletId) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Non connecté');
        const slippageBps = slippage === 'auto' ? 150 : Math.max(parseInt(slippage)*100, 50);
        const r = await fetch('https://copyccxx.com/api/wallet/buy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: session.user.id, walletId: vpsWalletId, tokenMint, amountSol: amt, slippageBps, jitoTip }),
        });
        const j = await r.json();
        if (!j.success) throw new Error(j.error || 'Erreur achat VPS');
        showMsg(`Achat envoyé ! TX: ${j.signature.slice(0,12)}...`);
      } else {
        const slippageBps=slippage==='auto'?50:parseInt(slippage)*100;
        const result=await buyToken({keypair,tokenMint,amountSol:amt,slippageBps,jitoTip});
        showMsg(`Achat envoyé ! TX: ${result.signature.slice(0,12)}...`);
      }
    } catch(e){ showMsg(e.message,'err'); }
    setBuyLoading(false);
  };

  const executeSell=async(tokenMint,pct,slippage='auto',jitoTip='low')=>{
    if(!keypair && !vpsWalletId && keypair !== 'sim') return showMsg('Wallet bot non accessible','err');
    if(!tokenMint) return showMsg('Aucun token sélectionné','err');
    setSellLoading(true);
    try {
      if(keypair === 'sim') {
        const pctNum = parseInt(pct) || 100;
        const result = await botEngine.executSimSell(tokenMint, pctNum, vpsWalletId || 'sim');
        showMsg(`Sim vente ${pct} — PNL: ${result.pnlUsd>=0?'+':''}${result.pnlUsd.toFixed(2)}$`);
      } else if(vpsWalletId) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error('Non connecté');
        const slippageBps = slippage === 'auto' ? 150 : Math.max(parseInt(slippage)*100, 100);
        const r = await fetch('https://copyccxx.com/api/wallet/sell', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: session.user.id, walletId: vpsWalletId, tokenMint, percentToSell: parseInt(pct)||100, slippageBps }),
        });
        const j = await r.json();
        if (!j.success) throw new Error(j.error || 'Erreur vente VPS');
        showMsg(`Vente ${pct} envoyée ! TX: ${j.signature.slice(0,12)}...`);
      } else {
        const slippageBps=slippage==='auto'?100:parseInt(slippage)*100;
        const result=await sellTokenPercent({keypair,tokenMint,percentToSell:parseInt(pct)||100,slippageBps,jitoTip});
        showMsg(`Vente ${pct} envoyée ! TX: ${result.signature.slice(0,12)}...`);
      }
    } catch(e){ showMsg(e.message,'err'); }
    setSellLoading(false);
  };

  return {buyLoading,sellLoading,msg,executeBuy,executeSell};
}

function Toggle({checked,onChange}) {
  return (
    <div onClick={e=>{e.stopPropagation();onChange(!checked);}} style={{width:36,height:20,borderRadius:10,background:checked?'#00e5cc':'#1a2030',border:`1px solid ${checked?'#00e5cc':'#2a3040'}`,cursor:'pointer',position:'relative',transition:'all 0.2s',flexShrink:0}}>
      <div style={{position:'absolute',top:2,left:checked?16:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'all 0.2s'}}/>
    </div>
  );
}

function TxMsg({msg}) {
  if(!msg) return null;
  return (
    <div style={{padding:'7px 10px',borderRadius:6,background:msg.type==='ok'?'rgba(0,229,204,0.08)':'rgba(255,107,107,0.08)',border:`1px solid ${msg.type==='ok'?'#00e5cc33':'#ff6b6b33'}`,fontSize:10,color:msg.type==='ok'?'#00e5cc':'#ff6b6b',lineHeight:1.5,wordBreak:'break-all'}}>
      {msg.text}
    </div>
  );
}

function InAppAlert({alerts, onDismiss}) {
  if(!alerts||!alerts.length) return null;
  return (
    <div style={{position:'fixed',top:70,right:16,zIndex:3000,display:'flex',flexDirection:'column',gap:6,maxWidth:320 }}>
      {alerts.map((a,i)=>(
        <div key={i} onClick={()=>onDismiss(i)}
          style={{background:'rgba(11,14,20,0.97)',border:`1px solid ${a.type==='buy'?'#00e5cc33':'#ff6b6b33'}`,borderRadius:8,padding:'10px 14px',cursor:'pointer',boxShadow:'0 4px 16px rgba(0,0,0,0.6)',display:'flex',alignItems:'flex-start',gap:10,animation:'slideIn 0.3s ease'}}>
          <span style={{fontSize:16,flexShrink:0}}>{a.type==='buy'?'🟢':'🔴'}</span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:700,color:a.type==='buy'?'#00e5cc':'#ff6b6b'}}>{a.type==='buy'?'Trade copié !':'Vente copiée !'}</div>
            <div style={{fontSize:10,color:'#c0c8d8',marginTop:2}}>{a.symbol} — {a.isSim?'SIM':'LIVE'}</div>
            <div style={{fontSize:9,color:'#5a6a80',marginTop:1}}>{a.detail}</div>
          </div>
          <span style={{fontSize:10,color:'#3a4a5a',flexShrink:0}}>×</span>
        </div>
      ))}
    </div>
  );
}

function ConfigModal({ onSave, onClose, initial, botWallet, walletList=[], balances={} }) {
  const [form,setForm]=useState({
    name:initial?.name||'', emoji:initial?.emoji||'', targetWallet:initial?.targetWallet||'',
    autoBuy:initial?.autoBuy??true, allowRebuy:initial?.allowRebuy??false,
    amountMode:initial?.amountMode||'fixed', amountValue:initial?.amountValue||'0.1',
    priorityFee:initial?.priorityFee||'medium', jitoTip:initial?.jitoTip||'low',
    slippage:initial?.slippage||'auto',
    copySells:initial?.copySells??false, status:initial?.status??true,
    stopLoss:initial?.stopLoss||'50', takeProfit:initial?.takeProfit||'200',
    stopLossEnabled:initial?.stopLossEnabled??true,
    takeProfitEnabled:initial?.takeProfitEnabled??true,
    trailingStopLossEnabled:initial?.trailingStopLossEnabled??false,
    trailingStopLoss:initial?.trailingStopLoss||'20',
    securityFilterEnabled:initial?.securityFilterEnabled??true,
    botWalletId:initial?.botWalletId||walletList[0]?.id||'',
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inp={background:'#0d1117',border:'1px solid #1e2a38',borderRadius:6,color:'#c0c8d8',fontSize:13,padding:'10px 14px',width:'100%',outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const sel={...inp,appearance:'none',cursor:'pointer'};
  const lbl={fontSize:10,color:'#00e5cc',letterSpacing:'1px',marginBottom:6,display:'block',fontFamily:'monospace'};
  const row={display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid #1a2030'};
  const sep=(t)=>(<div style={{display:'flex',alignItems:'center',gap:12,margin:'8px 0',color:'#3a4a5a',fontSize:11}}><div style={{flex:1,height:1,background:'#1a2030'}}/>{t}<div style={{flex:1,height:1,background:'#1a2030'}}/></div>);
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} onClick={onClose}>
      <div style={{background:'#0b0e14',border:'1px solid rgba(0,229,204,0.3)',borderRadius:10,width:480,maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',background:'#0d1018',borderBottom:'1px solid rgba(0,229,204,0.12)',position:'sticky',top:0,zIndex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{display:'flex',gap:5}}><span style={{width:12,height:12,borderRadius:'50%',background:'#ff5f57',display:'block'}}/><span style={{width:12,height:12,borderRadius:'50%',background:'#febc2e',display:'block'}}/><span style={{width:12,height:12,borderRadius:'50%',background:'#28c840',display:'block'}}/></div>
            <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',fontFamily:'monospace',letterSpacing:1}}>{initial?'Modifier config':'Nouvelle config'}</span>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#5a6a80',cursor:'pointer',fontSize:18}}>×</button>
        </div>
        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>
          <div><span style={lbl}>&gt; NOM</span><input style={inp} placeholder="ex. Whale 1"value={form.name} onChange={e=>set('name',e.target.value)}/></div>
          <div><span style={lbl}>&gt; EMOJI</span><input style={inp} placeholder="" value={form.emoji} onChange={e=>set('emoji',e.target.value)}/></div>
          <div><span style={lbl}>&gt; WALLET À COPIER</span><input style={inp} placeholder="Adresse Solana du wallet à suivre..."value={form.targetWallet} onChange={e=>set('targetWallet',e.target.value)}/></div>
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; COPIE AUTO LES ACHATS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Achète automatiquement quand le wallet suivi achète</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.autoBuy} onChange={v=>set('autoBuy',v)}/><span style={{fontSize:12,color:form.autoBuy?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.autoBuy?'OUI':'NON'}</span></div>
          </div>
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; AUTORISER LE RE-ACHAT</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Racheter un token déjà copié</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.allowRebuy} onChange={v=>set('allowRebuy',v)}/><span style={{fontSize:12,color:form.allowRebuy?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.allowRebuy?'OUI':'NON'}</span></div>
          </div>
          {!form.allowRebuy&&<div style={{background:'rgba(0,229,204,0.05)',border:'1px solid rgba(0,229,204,0.15)',borderRadius:6,padding:'10px 14px',fontSize:11,color:'#5a8a7a',fontStyle:'italic'}}>Un token déjà copié ne sera jamais re-copié</div>}
          <div>
            <span style={lbl}>&gt; WALLET UTILISÉ POUR LES ACHATS</span>
            <div style={{fontSize:9,color:'#3a4a5a',marginBottom:8}}>Quel wallet sera utilisé pour exécuter les trades ?</div>
            {walletList.length>0?(
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {walletList.map(w=>{
                  const bal = w.isSim ? (balances['sim']||0) : (balances[w.id]||0);
                  const isSelected=form.botWalletId===w.id;
                  return (
                    <div key={w.id} onClick={()=>set('botWalletId',w.id)}
                      style={{background:'#0d1117',border:`1px solid ${isSelected?(w.isSim?'#7c4dff44':'#00e5cc44'):'#1a2030'}`,borderRadius:6,padding:'10px 14px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',transition:'all 0.15s'}}>
                      <div>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          {w.isSim&&<span style={{fontSize:11}}></span>}
                          <div style={{fontSize:12,fontWeight:700,color:isSelected?(w.isSim?'#7c4dff':'#00e5cc'):'#c0c8d8'}}>{w.name}</div>
                          {w.isSim&&<span style={{fontSize:9,padding:'1px 5px',borderRadius:6,background:'rgba(124,77,255,0.15)',color:'#7c4dff',border:'1px solid #7c4dff33',fontWeight:700}}>SIMULATION</span>}
                        </div>
                        <div style={{fontSize:9,color:'#5a6a80',fontFamily:'monospace',marginTop:2}}>
                          {w.isSim?'Entraînement — aucun vrai SOL dépensé':`${w.address.slice(0,8)}...${w.address.slice(-4)}`}
                        </div>
                      </div>
                      <div style={{textAlign:'right',display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3}}>
                        <span style={{fontSize:11,fontWeight:700,color:isSelected?(w.isSim?'#7c4dff':'#00e5cc'):'#8a9ab0',fontFamily:'monospace'}}>{bal.toFixed(4)} SOL</span>
                        {isSelected&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:8,background:w.isSim?'rgba(124,77,255,0.15)':'rgba(0,229,204,0.15)',color:w.isSim?'#7c4dff':'#00e5cc',border:`1px solid ${w.isSim?'#7c4dff33':'#00e5cc33'}`}}> SÉLECTIONNÉ</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ):(
              <div style={{background:'rgba(255,107,107,0.05)',border:'1px solid #ff6b6b33',borderRadius:6,padding:'12px 14px',fontSize:12,color:'#ff6b6b'}}> Aucun wallet bot — crée-en un via  BOT</div>
            )}
          </div>
          {sep('MONTANT PAR TRADE')}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><span style={lbl}>&gt; TYPE</span><select style={sel} value={form.amountMode} onChange={e=>set('amountMode',e.target.value)}><option value="fixed">Montant fixe (SOL)</option><option value="percentage">% du solde</option></select></div>
            <div><span style={lbl}>&gt; VALEUR</span><div style={{display:'flex'}}><input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none'}} type="number"value={form.amountValue} onChange={e=>set('amountValue',e.target.value)}/><div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#8a9ab0',fontSize:12,fontFamily:'monospace'}}>{form.amountMode==='percentage'?'%':'SOL'}</div></div></div>
          </div>
          {sep('PARAMÈTRES AVANCÉS')}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><span style={lbl}>&gt; PRIORITÉ TX</span><select style={sel} value={form.priorityFee} onChange={e=>set('priorityFee',e.target.value)}><option value="low">Faible</option><option value="medium">Moyenne</option><option value="high">Haute</option><option value="very_high">Très haute</option></select></div>
            <div><span style={lbl}>&gt; JITO TIP</span><select style={sel} value={form.jitoTip} onChange={e=>set('jitoTip',e.target.value)}><option value="low">Faible</option><option value="medium">Moyen</option><option value="high">Haut</option></select></div>
          </div>
          <div><span style={lbl}>&gt; SLIPPAGE</span><select style={sel} value={form.slippage} onChange={e=>set('slippage',e.target.value)}><option value="auto">Auto</option><option value="1">1%</option><option value="5">5%</option><option value="10">10%</option><option value="20">20%</option></select></div>
          {sep('VENTES')}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; COPIER LES VENTES</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:3}}>Vend automatiquement quand le wallet suivi vend</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.copySells} onChange={v=>set('copySells',v)}/>
              <span style={{fontSize:12,color:form.copySells?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.copySells?'OUI':'NON'}</span>
            </div>
          </div>
          {sep('PROTECTION')}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#ff6b6b',fontFamily:'monospace'}}>&gt; STOP-LOSS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vente automatique en cas de perte</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.stopLossEnabled} onChange={v=>set('stopLossEnabled',v)}/>
              <span style={{fontSize:12,color:form.stopLossEnabled?'#ff6b6b':'#5a6a80',fontFamily:'monospace'}}>{form.stopLossEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.stopLossEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#ff6b6b'}} type="number"placeholder="50"value={form.stopLoss} onChange={e=>set('stopLoss',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#ff6b6b',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si la perte dépasse {form.stopLoss}%</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#00e5cc',fontFamily:'monospace'}}>&gt; TAKE-PROFIT</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vente automatique pour sécuriser les gains</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.takeProfitEnabled} onChange={v=>set('takeProfitEnabled',v)}/>
              <span style={{fontSize:12,color:form.takeProfitEnabled?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.takeProfitEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.takeProfitEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#00e5cc'}} type="number"placeholder="200"value={form.takeProfit} onChange={e=>set('takeProfit',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#00e5cc',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si le gain dépasse {form.takeProfit}%</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#f0a500',fontFamily:'monospace'}}>&gt; TRAILING STOP-LOSS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Stop qui suit le prix à la hausse</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.trailingStopLossEnabled} onChange={v=>set('trailingStopLossEnabled',v)}/>
              <span style={{fontSize:12,color:form.trailingStopLossEnabled?'#f0a500':'#5a6a80',fontFamily:'monospace'}}>{form.trailingStopLossEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.trailingStopLossEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#f0a500'}} type="number" placeholder="20" value={form.trailingStopLoss} onChange={e=>set('trailingStopLoss',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#f0a500',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si le prix chute de {form.trailingStopLoss}% depuis le plus haut atteint</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#7c4dff',fontFamily:'monospace'}}>&gt; FILTRE DE SÉCURITÉ</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vérifie si le token est un rug/honeypot avant d'acheter</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.securityFilterEnabled} onChange={v=>set('securityFilterEnabled',v)}/>
              <span style={{fontSize:12,color:form.securityFilterEnabled?'#7c4dff':'#5a6a80',fontFamily:'monospace'}}>{form.securityFilterEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.securityFilterEnabled&&(
            <div style={{background:'rgba(124,77,255,0.05)',border:'1px solid #7c4dff22',borderRadius:6,padding:'10px 14px',fontSize:11,color:'#5a4a80'}}>
              🛡 Vérifie via RugCheck avant chaque achat — ignore les tokens dangereux automatiquement
            </div>
          )}
          {sep('')}
          <div style={row}><span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; STATUT</span><div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.status} onChange={v=>set('status',v)}/><span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,fontFamily:'monospace'}}><span style={{width:7,height:7,borderRadius:'50%',background:form.status?'#00e5cc':'#3a4a5a',display:'inline-block'}}/><span style={{color:form.status?'#00e5cc':'#5a6a80'}}>{form.status?'ACTIF':'INACTIF'}</span></span></div></div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:12,padding:'16px 22px',borderTop:'1px solid #1a2030',position:'sticky',bottom:0,background:'#0b0e14'}}>
          <button onClick={onClose} style={{padding:'9px 20px',borderRadius:6,background:'transparent',border:'1px solid #1e2530',color:'#5a6a80',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'monospace'}}>Annuler</button>
          <button onClick={()=>onSave(form)} style={{padding:'9px 20px',borderRadius:6,background:'#00e5cc',border:'none',color:'#060809',fontSize:12,fontWeight:800,cursor:'pointer',fontFamily:'monospace'}}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}function ConfigsPanel({ configs, selectedId, setSelectedId, toggleConfig, setEditConfig, setShowModal, deleteConfig, onGoToNetwork }) {
  const hasConfigs = configs.length > 0;
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}>MES CONFIGS</span>
        <button onClick={()=>{setEditConfig(null);setShowModal(true);}}
          style={{padding:'4px 10px',borderRadius:5,background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
          + Nouvelle
        </button>
      </div>
      {!hasConfigs?(
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:24,gap:20}}>
          <div style={{fontSize:32,opacity:0.3}}></div>
          <div style={{fontSize:13,fontWeight:700,color:'#4a5a70',textAlign:'center'}}>Aucune config</div>
          <div style={{display:'flex',flexDirection:'column',gap:10,width:'100%'}}>
            {[['1','Crée une config','Clique "+ Nouvelle"en haut'],['2','Entre le wallet à copier','L\'adresse Solana du trader'],['3','Active le toggle','Le bot commence à copier']].map(([n,title,sub])=>(
              <div key={n} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:'#0d1117',border:'1px solid #1a2030',borderRadius:8}}>
                <span style={{width:20,height:20,borderRadius:'50%',background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:10,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{n}</span>
                <div><div style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{title}</div><div style={{fontSize:10,color:'#3a4a5a',marginTop:2}}>{sub}</div></div>
              </div>
            ))}
          </div>
          <button onClick={()=>setShowModal(true)} style={{width:'100%',padding:'12px',borderRadius:8,background:'linear-gradient(135deg,#00e5cc,#00b8a0)',border:'none',color:'#060809',fontSize:13,fontWeight:800,cursor:'pointer'}}>+ Créer ma première config</button>
        </div>
      ):(
        <div style={{overflowY:'auto',flex:1}}>
          {configs.map(c=>{
            const isSelected = selectedId===c.id;
            return (
              <div key={c.id} onClick={()=>setSelectedId(c.id===selectedId?null:c.id)}
                style={{borderBottom:'1px solid #0f1520',cursor:'pointer',background:isSelected?'rgba(0,229,204,0.04)':'transparent',borderLeft:`2px solid ${isSelected?'#00e5cc':'transparent'}`}}>
                <div style={{padding:'10px 14px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:c.enabled?'#00e5cc':'#2a3a4a',flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.emoji||''} {c.name}</span>
                    {c.botWalletId&&<span style={{fontSize:9,padding:'1px 5px',borderRadius:4,background:c.botWalletId==='sim'?'rgba(124,77,255,0.12)':'rgba(0,229,204,0.08)',color:c.botWalletId==='sim'?'#7c4dff':'#00e5cc',border:`1px solid ${c.botWalletId==='sim'?'#7c4dff22':'#00e5cc22'}`,fontWeight:700,flexShrink:0}}>{c.botWalletId==='sim'?'SIM':'LIVE'}</span>}
                    <div onClick={e=>e.stopPropagation()} style={{flexShrink:0}}><Toggle checked={c.enabled} onChange={()=>toggleConfig(c.id)}/></div>
                  </div>
                  <div style={{fontSize:10,color:'#3a4a5a',fontFamily:'monospace',paddingLeft:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.targetWallet?`${c.targetWallet.slice(0,12)}...${c.targetWallet.slice(-4)}`:'Aucun wallet configuré'}
                  </div>
                  {c.enabled&&c.targetWallet&&c.targetWallet.length>10&&(
                    <div style={{display:'flex',alignItems:'center',gap:4,marginTop:4,paddingLeft:12}}>
                      <span style={{width:5,height:5,borderRadius:'50%',background:'#00e5cc',animation:'pulse 2s infinite'}}/>
                      <span style={{fontSize:10,color:'#00e5cc'}}>En écoute</span>
                    </div>
                  )}
                  <div style={{display:'flex',gap:6,paddingLeft:12,marginTop:3,flexWrap:'wrap'}}>
                    {c.amountValue&&<span style={{fontSize:9,color:'#5a6a80'}}> {c.amountValue}{c.amountMode==='percentage'?'%':'SOL'}</span>}
                    {c.stopLossEnabled!==false&&c.stopLoss&&<span style={{fontSize:9,color:'#ff6b6b'}}> SL -{c.stopLoss}%</span>}
                    {c.takeProfitEnabled!==false&&c.takeProfit&&<span style={{fontSize:9,color:'#00e5cc'}}>🟢 TP +{c.takeProfit}%</span>}
                  </div>
                </div>
                {isSelected&&(
                  <div style={{display:'flex',gap:6,padding:'0 14px 10px',paddingLeft:26}}>
                    <button onClick={e=>{e.stopPropagation();setEditConfig(c);setShowModal(true);}} style={{flex:1,padding:'6px',borderRadius:5,background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',color:'#00e5cc',fontSize:10,fontWeight:700,cursor:'pointer'}}> Modifier</button>
                    <button onClick={e=>{e.stopPropagation();if(window.confirm('Supprimer cette config ?'))deleteConfig(c.id);}} style={{padding:'6px 10px',borderRadius:5,background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',color:'#ff6b6b',fontSize:10,cursor:'pointer'}}></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SuivreLeftPanel({ configs, selectedConfig, onSelect, onTokenFound }) {
  const [tradesMap,setTradesMap]=useState({});
  const [loadingMap,setLoadingMap]=useState({});
  const activeConfigs=configs.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10);

  useEffect(()=>{
    if(!activeConfigs.length) return;
    const fetchAll=async()=>{
      for(const c of activeConfigs){
        setLoadingMap(m=>({...m,[c.id]:true}));
        const trades=await fetchWalletTrades(c.targetWallet,8);
        setTradesMap(m=>({...m,[c.id]:trades}));
        setLoadingMap(m=>({...m,[c.id]:false}));
      }
    };
    fetchAll();
    const iv=setInterval(fetchAll,15000);
    return()=>clearInterval(iv);
  },[configs]);

  const handleSelect=async(c,mint)=>{
    onSelect(c); onTokenFound(null);
    const targetMint=mint||tradesMap[c.id]?.find(t=>t.type==='BUY')?.mint||await fetchLastBoughtMint(c.targetWallet);
    if(targetMint){ const info=await fetchTokenInfo(targetMint); onTokenFound(info); }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}>ACTIVITÉ EN DIRECT</span>
          <div style={{fontSize:9,color:'#2a3a4a',marginTop:2}}>Transactions des wallets suivis</div>
        </div>
        <span style={{fontSize:9,color:'#3a4a5a',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,padding:'2px 6px'}}>↻ 15s</span>
      </div>
      {activeConfigs.length>0&&(
        <div style={{padding:'6px 10px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
          {activeConfigs.map((c)=>(
            <button key={c.id} onClick={()=>handleSelect(c)}
              style={{padding:'3px 8px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',background:selectedConfig?.id===c.id?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${selectedConfig?.id===c.id?'#00e5cc55':'#1a2030'}`,color:selectedConfig?.id===c.id?'#00e5cc':'#5a6a80',maxWidth:90,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {c.emoji||''} {c.name}
            </button>
          ))}
        </div>
      )}
      {activeConfigs.length===0?(
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12,padding:24}}>
          <div style={{fontSize:28,opacity:0.2}}></div>
          <div style={{fontSize:12,fontWeight:600,color:'#3a4a5a',textAlign:'center'}}>Aucun wallet suivi</div>
          <div style={{fontSize:11,color:'#2a3a4a',textAlign:'center',lineHeight:1.6}}>Va dans <strong style={{color:'#5a6a80'}}> Configs</strong> et active une config pour voir les trades en temps réel ici.</div>
        </div>
      ):(
        <div style={{flex:1,overflowY:'auto'}}>
          {activeConfigs.map((c)=>{
            const trades=tradesMap[c.id]||[]; const loading=loadingMap[c.id]; const isSel=selectedConfig?.id===c.id;
            return (
              <div key={c.id}>
                <div onClick={()=>handleSelect(c)} style={{padding:'8px 12px',borderBottom:'1px solid #0f1520',cursor:'pointer',background:isSel?'rgba(0,229,204,0.05)':'#090c12',borderLeft:`2px solid ${isSel?'#00e5cc':'transparent'}`,display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:11,fontWeight:800,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'2px 8px',flexShrink:0,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.emoji||''} {c.name}
                  </span>
                  <span style={{fontSize:9,color:'#3a4a5a',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontFamily:'monospace'}}>{c.targetWallet?.slice(0,8)}...</span>
                  {loading&&<span style={{fontSize:9,color:'#3a4a5a'}}>...</span>}
                  <span style={{width:5,height:5,borderRadius:'50%',background:'#00e5cc',flexShrink:0}}/>
                </div>
                {trades.slice(0,5).map((t,j)=>{
                  const color=t.type==='BUY'?'#00e5cc':'#ff6b6b';
                  return (
                    <div key={t.signature+j} onClick={()=>handleSelect(c,t.mint)}
                      style={{padding:'6px 12px 6px 24px',borderBottom:'1px solid #0a0f18',cursor:'pointer',display:'flex',alignItems:'center',gap:6}}
                      onMouseEnter={e=>e.currentTarget.style.background='#0d1018'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <span style={{fontSize:9,padding:'1px 5px',borderRadius:6,background:`${color}18`,color,border:`1px solid ${color}30`,fontWeight:700,flexShrink:0}}>{t.type}</span>
                      <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.symbol}</span>
                      <span style={{fontSize:10,color:'#5a6a80',flexShrink:0}}>{t.solDelta.toFixed(3)} SOL</span>
                      <span style={{fontSize:9,color:'#3a4a5a',flexShrink:0}}>{timeAgo(t.timestamp)}</span>
                    </div>
                  );
                })}
                {trades.length===0&&!loading&&<div style={{padding:'8px 24px',fontSize:10,color:'#2a3a4a',fontStyle:'italic'}}>Aucun trade récent</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrackCenterPanel({ selectedConfig, tokenInfo, tokenLoading, keypair, onTokenFound }) {
  const [amount,setAmount]=useState('0.1');
  const [slippage,setSlippage]=useState('auto');
  const [jitoTip,setJitoTip]=useState('low');
  const [searchMint,setSearchMint]=useState('');
  const [searching,setSearching]=useState(false);
  const {loading,msg,executeBuy}=useManualTrade(keypair);
  const QUICK=['0.01','0.05','0.1','0.25','0.5','1'];
  const chartUrl=tokenInfo?.mint?`https://birdeye.so/tv-widget/${tokenInfo.mint}?chain=solana&viewMode=pair&autoLoad=true&theme=dark`:null;

  const handleSearch=async()=>{
    if(!searchMint.trim()) return;
    setSearching(true);
    const info=await fetchTokenInfo(searchMint.trim());
    if(info) onTokenFound(info);
    else onTokenFound(null);
    setSearching(false);
  };

  if(!selectedConfig) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:12}}>
      <div style={{fontSize:40,opacity:0.2}}></div>
      <div style={{fontSize:14,fontWeight:600,color:'#3a4a5a'}}>Sélectionne un wallet à gauche</div>
      <div style={{fontSize:12,color:'#2a3a4a'}}>Clique sur une transaction pour voir le graphique</div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'8px 16px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',flexShrink:0}}>
        {tokenInfo?(<>
          <div style={{width:26,height:26,borderRadius:6,background:'linear-gradient(135deg,#00e5cc22,#7c4dff22)',border:'1px solid #00e5cc33',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:'#00e5cc',flexShrink:0}}>{tokenInfo.symbol?.charAt(0)||'?'}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:800,color:'#e0e8f0'}}>{tokenInfo.symbol} <span style={{fontSize:10,color:'#5a6a80',fontWeight:400}}>{tokenInfo.name}</span></div>
            <div style={{fontSize:9,color:'#3a4a5a',fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{tokenInfo.mint?.slice(0,20)}...</div>
          </div>
          <div style={{display:'flex',gap:12,fontSize:10,flexShrink:0}}>
            {[['MCAP',`$${fmt(tokenInfo.mcap)}`],['LIQ',`$${fmt(tokenInfo.liq)}`],['24H',`${tokenInfo.change24h>=0?'+':''}${tokenInfo.change24h?.toFixed(1)}%`]].map(([l,v])=>(
              <div key={l} style={{textAlign:'center'}}><div style={{fontSize:8,color:'#3a4a5a',marginBottom:1}}>{l}</div><div style={{fontWeight:700,color:l==='24H'?(tokenInfo.change24h>=0?'#00e5cc':'#ff6b6b'):'#c0c8d8'}}>{v}</div></div>
            ))}
          </div>
          <a href={tokenInfo.dexUrl} target="_blank"rel="noreferrer"style={{fontSize:10,color:'#00e5cc',textDecoration:'none',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'3px 8px',flexShrink:0}}>DexScreener ↗</a>
        </>):tokenLoading?(
          <div style={{fontSize:12,color:'#3a4a5a',display:'flex',alignItems:'center',gap:8}}><div style={{width:14,height:14,borderRadius:'50%',border:'2px solid #1a2030',borderTop:'2px solid #00e5cc',animation:'spin 1s linear infinite'}}/>Détection du dernier token acheté...</div>
        ):(
          <div style={{fontSize:12,color:'#3a4a5a'}}>Clique sur un trade dans le panneau gauche</div>
        )}
      </div>
      <div style={{padding:'6px 12px',borderBottom:'1px solid #1a2030',display:'flex',gap:6,alignItems:'center',background:'#090c12'}}>
        <input placeholder="Colle une adresse mint Solana..."value={searchMint} onChange={e=>setSearchMint(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSearch()}
          style={{flex:1,background:'#0d1117',border:'1px solid #1a2030',borderRadius:5,color:'#c0c8d8',fontSize:10,padding:'5px 8px',outline:'none',fontFamily:'monospace'}}/>
        <button onClick={handleSearch} disabled={searching||!searchMint.trim()}
          style={{padding:'5px 10px',borderRadius:5,background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:10,fontWeight:700,cursor:'pointer',flexShrink:0}}>
          {searching?'⏳':''}
        </button>
      </div>
      <div style={{flex:1,position:'relative',minHeight:0,overflow:'hidden'}}>
        {chartUrl?<iframe key={chartUrl} src={chartUrl} style={{width:'100%',height:'100%',border:'none',display:'block'}} title="Birdeye"/>:(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:10}}>
            {tokenLoading?<><div style={{width:28,height:28,borderRadius:'50%',border:'2px solid #1a2030',borderTop:'2px solid #00e5cc',animation:'spin 1s linear infinite'}}/><span style={{fontSize:12}}>Chargement...</span></>:<><span style={{fontSize:40,opacity:0.2}}></span><span style={{fontSize:12}}>Sélectionne un trade à gauche ou colle un mint</span></>}
          </div>
        )}
      </div>
      {tokenInfo&&(
        <div style={{padding:'8px 12px',borderTop:'1px solid #1a2030',background:'#090c12',flexShrink:0}}>
          <TxMsg msg={msg}/>
          <div style={{display:'flex',alignItems:'center',gap:6,marginTop:msg?6:0}}>
            <span style={{fontSize:10,color:'#5a6a80',flexShrink:0}}>Buy:</span>
            {QUICK.map(q=>(<button key={q} onClick={()=>setAmount(q)} style={{padding:'3px 7px',borderRadius:4,background:amount===q?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${amount===q?'#00e5cc44':'#1a2030'}`,color:amount===q?'#00e5cc':'#5a6a80',fontSize:10,fontWeight:600,cursor:'pointer'}}>{q}</button>))}
            <select value={slippage} onChange={e=>setSlippage(e.target.value)} style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#5a6a80',fontSize:9,padding:'3px 4px',outline:'none',cursor:'pointer'}}>
              <option value="auto">Auto slip</option><option value="1">1%</option><option value="5">5%</option><option value="10">10%</option>
            </select>
            <button onClick={()=>executeBuy(tokenInfo.mint,amount,slippage,jitoTip)} disabled={loading||!keypair}
              style={{marginLeft:'auto',padding:'5px 14px',borderRadius:5,background:loading?'#0d1117':'linear-gradient(135deg,#00e5cc,#00b8a0)',border:loading?'1px solid #1a2030':'none',color:loading?'#5a6a80':'#060809',fontSize:11,fontWeight:800,cursor:loading?'not-allowed':'pointer',flexShrink:0,opacity:!keypair?0.5:1}}>
              {loading?'⏳...':`Buy ${amount} SOL`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PositionsPanel({ botStats, selectedPosition, onSelectPosition, allTrades, configs }) {
  const [monTab,setMonTab]=useState('ouvertes');
  const [histFilter,setHistFilter]=useState('all');
  const [closingAll,setClosingAll]=useState(false);
  const [refreshKey,setRefreshKey]=useState(0);
  const [livePrices,setLivePrices]=useState({});
  const positions=botStats.positions||[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localTrades=useMemo(()=>JSON.parse(localStorage.getItem('bot-trades')||'[]'),[refreshKey]);
  const trades = allTrades && allTrades.length > 0 ? allTrades : localTrades;

  const closePosition = async (p) => {
    // bot-trades
    const trades2 = JSON.parse(localStorage.getItem('bot-trades')||'[]').filter(t => !(t.mint===p.mint && t.resultat==='En cours' && t.isSim));
    localStorage.setItem('bot-trades', JSON.stringify(trades2));
    // bot-stats
    const stats = JSON.parse(localStorage.getItem('bot-stats')||'{}');
    if (stats.positions) { stats.positions = stats.positions.filter(pos => !(pos.mint===p.mint && pos.walletIsSim)); localStorage.setItem('bot-stats', JSON.stringify(stats)); }
    // Supabase
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await supabase.from('trades').delete().eq('user_id', session.user.id).eq('mint', p.mint).eq('resultat', 'En cours').eq('is_sim', true);
    } catch(e) {}
    setRefreshKey(k => k+1);
  };

  const closeAllSim = async () => {
    setClosingAll(true);
    // Vider complètement les deux clés localStorage
    localStorage.removeItem('bot-trades');
    localStorage.removeItem('bot-stats');
    // Supabase
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await supabase.from('trades').delete().eq('user_id', session.user.id).eq('resultat', 'En cours').eq('is_sim', true);
    } catch(e) {}
    setRefreshKey(k => k+1);
    setClosingAll(false);
  };
  const closed=trades.filter(t=>t.resultat&&t.resultat!=='En cours');
  const closedFiltered = histFilter==='sim'?closed.filter(t=>t.isSim):histFilter==='live'?closed.filter(t=>!t.isSim):closed;

  const rawSimTrades = trades.filter(t=>t.resultat==='En cours'&&t.isSim);
  const simMints = new Set(positions.filter(p=>p.walletIsSim).map(p=>p.mint));
  const instantSimPositions = rawSimTrades
    .filter(t=>!simMints.has(t.mint))
    .map(t=>({
      mint:t.mint, symbol:t.symbol||'?', tokenAmount:t.tokenAmount||0,
      achatUsd:t.achatUsd, currentUsd:t.achatUsd, pnlUsd:0, pnlPct:0,
      botWalletId:t.botWalletId, walletName:'Sim Wallet', walletIsSim:true,
    }));
  const rawLiveTrades = trades.filter(t=>t.resultat==='En cours'&&!t.isSim);
  const liveMints = new Set(positions.filter(p=>!p.walletIsSim).map(p=>p.mint));
  const instantLivePositions = rawLiveTrades
    .filter(t=>!liveMints.has(t.mint))
    .map(t=>{
      const matchCfg = configs?.find(c=>c.targetWallet===t.walletSuivi);
      return {
        mint:t.mint, symbol:t.symbol||'?', tokenAmount:t.tokenAmount||0,
        achatUsd:t.achatUsd, currentUsd:t.achatUsd, pnlUsd:0, pnlPct:0,
        prixEntree:t.prixEntree||0, entreeTs:t.entreeTs,
        botWalletId:matchCfg?.botWalletId||null, walletName:'Live', walletIsSim:false,
      };
    });
  const allPositions=[...positions,...instantSimPositions,...instantLivePositions];
  const simPositions=allPositions.filter(p=>p.walletIsSim);
  const livePositions=allPositions.filter(p=>!p.walletIsSim);

  useEffect(()=>{
    const openMints=[...instantLivePositions,...instantSimPositions].filter(p=>p.prixEntree>0).map(p=>p.mint);
    if(!openMints.length) return;
    const fetchPrices=async()=>{
      const newPrices={};
      for(const pos of [...instantLivePositions,...instantSimPositions]){
        if(!pos.mint||!pos.prixEntree) continue;
        try{
          const r=await fetch('https://api.dexscreener.com/latest/dex/tokens/'+pos.mint);
          const j=await r.json();
          const pair=j?.pairs?.[0];
          if(pair?.priceUsd){
            const cur=parseFloat(pair.priceUsd);
            const pnlPct=((cur-pos.prixEntree)/pos.prixEntree)*100;
            const pnlUsd=(pos.achatUsd||0)*(pnlPct/100);
            newPrices[pos.mint]={pnlPct,pnlUsd,symbol:pair.baseToken?.symbol||pos.symbol};
          }
        }catch{}
      }
      setLivePrices(newPrices);
    };
    fetchPrices();
    const iv=setInterval(fetchPrices,15000);
    return()=>clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[instantLivePositions.length,instantSimPositions.length]);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030'}}><span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}>MES POSITIONS</span></div>
      <div style={{display:'flex',borderBottom:'1px solid #1a2030'}}>
        <button onClick={()=>setMonTab('ouvertes')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='ouvertes'?'#00e5cc':'transparent'}`,color:monTab==='ouvertes'?'#00e5cc':'#4a5a70'}}>
          LIVE <span style={{fontSize:9,background:monTab==='ouvertes'?'#00e5cc':'#1a2030',color:monTab==='ouvertes'?'#060809':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{livePositions.length}</span>
        </button>
        <button onClick={()=>setMonTab('sim')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='sim'?'#7c4dff':'transparent'}`,color:monTab==='sim'?'#7c4dff':'#4a5a70'}}>
          SIM <span style={{fontSize:9,background:monTab==='sim'?'#7c4dff':'#1a2030',color:monTab==='sim'?'#fff':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{simPositions.length}</span>
        </button>
        <button onClick={()=>setMonTab('fermees')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='fermees'?'#00e5cc':'transparent'}`,color:monTab==='fermees'?'#00e5cc':'#4a5a70'}}>
          HIST. <span style={{fontSize:9,background:monTab==='fermees'?'#00e5cc':'#1a2030',color:monTab==='fermees'?'#060809':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{closed.length}</span>
        </button>
      </div>
      <div style={{flex:1,overflowY:'auto'}}>
        {monTab==='ouvertes'&&(livePositions.length===0?(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:8,padding:20}}>
            <div style={{fontSize:28,opacity:0.3}}></div>
            <div style={{fontSize:12,textAlign:'center',color:'#3a4a5a'}}>Aucune position live</div>
          </div>
        ):livePositions.map((p,i)=>(<PositionItem key={i} p={p} selectedPosition={selectedPosition} onSelectPosition={onSelectPosition} livePrice={livePrices[p.mint]}/>)))}
        {monTab==='sim'&&(simPositions.length===0?(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:8,padding:20}}>
            <div style={{fontSize:28,opacity:0.3}}></div>
            <div style={{fontSize:12,textAlign:'center',color:'#3a4a5a'}}>Aucune position simulée</div>
          </div>
        ):(<>
          {simPositions.length>1&&<div style={{padding:'6px 10px',borderBottom:'1px solid #1a2030',display:'flex',justifyContent:'flex-end'}}>
            <button onClick={closeAllSim} disabled={closingAll} style={{padding:'3px 10px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:'rgba(255,107,107,0.1)',border:'1px solid #ff6b6b33',color:'#ff6b6b'}}>
              {closingAll?'...':'✕ Tout fermer'}
            </button>
          </div>}
          {simPositions.map((p,i)=>(<PositionItem key={i} p={p} selectedPosition={selectedPosition} onSelectPosition={onSelectPosition} onClosePosition={closePosition} livePrice={livePrices[p.mint]}/>))}
        </>))}
        {monTab==='fermees'&&(<>
          <div style={{padding:'6px 10px',borderBottom:'1px solid #1a2030',display:'flex',gap:4}}>
            {[['all','Tous'],['live','Live'],['sim','Sim']].map(([v,l])=>(
              <button key={v} onClick={()=>setHistFilter(v)}
                style={{padding:'3px 8px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:histFilter===v?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${histFilter===v?'#00e5cc44':'#1a2030'}`,color:histFilter===v?'#00e5cc':'#5a6a80'}}>
                {l}
              </button>
            ))}
            <span style={{marginLeft:'auto',fontSize:9,color:'#3a4a5a',alignSelf:'center'}}>{closedFiltered.length} trades</span>
          </div>
          {closedFiltered.length===0?(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'80%',color:'#3a4a5a',gap:8,padding:20}}>
              <div style={{fontSize:28,opacity:0.3}}></div>
              <div style={{fontSize:12}}>Aucun trade fermé</div>
            </div>
          ):closedFiltered.map((t,i)=>(
            <div key={i} onClick={()=>onSelectPosition({...t,walletIsSim:t.isSim||false})}
              style={{padding:'10px 14px',borderBottom:'1px solid #0f1520',borderLeft:`3px solid ${(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}`,cursor:'pointer'}}
              onMouseEnter={e=>e.currentTarget.style.background='#0d1018'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                <div style={{width:20,height:20,borderRadius:4,background:t.isSim?'rgba(124,77,255,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${t.isSim?'#7c4dff22':'#00e5cc22'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,color:t.isSim?'#7c4dff':'#00e5cc'}}>{t.symbol?.charAt(0)||'?'}</div>
                <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',flex:1}}>{t.symbol}</span>
                <span style={{fontSize:9,padding:'1px 5px',borderRadius:4,background:t.isSim?'rgba(124,77,255,0.12)':'rgba(0,229,204,0.08)',color:t.isSim?'#7c4dff':'#00e5cc',border:`1px solid ${t.isSim?'#7c4dff22':'#00e5cc22'}`,fontWeight:700}}>{t.isSim?'':''}</span>
                <span style={{fontSize:12,fontWeight:800,color:(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}}>{(t.pnlUsd||0)>=0?'+':''}{t.roi?.toFixed(1)}%</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#3a4a5a',paddingLeft:26}}>
                <span style={{color:(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>{(t.pnlUsd||0)>=0?'+':''}{(t.pnlUsd||0).toFixed(2)}$</span>
                <span style={{color:'#5a6a80'}}>investi {(t.achatUsd||0).toFixed(2)}$</span>
                {t.entreeTs&&<span> {duration(t.entreeTs, t.sortieTs)}</span>}
              </div>
            </div>
          ))}
        </>)}
      </div>
    </div>
  );
}

function PositionItem({ p, selectedPosition, onSelectPosition, onClosePosition, livePrice }) {
  const isSelected = selectedPosition?.mint===p.mint&&selectedPosition?.botWalletId===p.botWalletId;
  const pnlPct = livePrice?.pnlPct ?? p.pnlPct ?? 0;
  const pnlUsd = livePrice?.pnlUsd ?? p.pnlUsd ?? 0;
  const symbol = livePrice?.symbol || p.symbol;
  const pnlColor = pnlPct>=0?'#00e5cc':'#ff6b6b';
  const barPct = Math.min(100, Math.max(0, (pnlPct + 100) / 6));
  const isLive = !!livePrice;
  return (
    <div onClick={()=>onSelectPosition({...p, pnlPct, pnlUsd, symbol})}
      style={{padding:'10px 14px',borderBottom:'1px solid #0f1520',cursor:'pointer',borderLeft:`3px solid ${pnlColor}`,background:isSelected?'rgba(0,229,204,0.04)':'transparent',position:'relative'}}
      onMouseEnter={e=>e.currentTarget.style.background='#0d1018'}
      onMouseLeave={e=>e.currentTarget.style.background=isSelected?'rgba(0,229,204,0.04)':'transparent'}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
        <div style={{width:22,height:22,borderRadius:4,background:p.walletIsSim?'rgba(124,77,255,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${p.walletIsSim?'#7c4dff22':'#00e5cc22'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:p.walletIsSim?'#7c4dff':'#00e5cc'}}>{symbol?.charAt(0)||'?'}</div>
        <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',flex:1}}>{symbol}</span>
        {p.walletName&&<span style={{fontSize:8,padding:'1px 5px',borderRadius:4,background:p.walletIsSim?'rgba(124,77,255,0.12)':'rgba(0,229,204,0.08)',color:p.walletIsSim?'#7c4dff':'#00e5cc',border:`1px solid ${p.walletIsSim?'#7c4dff22':'#00e5cc22'}`,fontWeight:700,flexShrink:0}}>{p.walletIsSim?'SIM':'LIVE'}</span>}
        <span style={{fontSize:11,fontWeight:800,color:pnlColor}}>{pnlPct>=0?'+':''}{pnlPct.toFixed(1)}%</span>
        {isLive&&<span style={{width:5,height:5,borderRadius:'50%',background:'#00e5cc',animation:'pulse 2s infinite',flexShrink:0}}/>}
        {p.walletIsSim && onClosePosition && (
          <button onClick={e=>{e.stopPropagation();onClosePosition(p);}}
            title="Fermer la position"
            style={{width:16,height:16,borderRadius:3,background:'rgba(255,107,107,0.15)',border:'1px solid #ff6b6b33',color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,padding:0,lineHeight:1}}>✕</button>
        )}
      </div>
      <div style={{height:3,background:'#1a2030',borderRadius:2,marginBottom:4,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${barPct}%`,background:pnlColor,borderRadius:2,transition:'width 0.3s'}}/>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,paddingLeft:28}}>
        <span style={{color:'#3a4a5a'}}>{p.achatUsd?.toFixed(2)}$ investi</span>
        <span style={{color:pnlColor,fontWeight:700}}>{pnlUsd>=0?'+':''}{pnlUsd.toFixed(2)}$</span>
      </div>
    </div>
  );
}

function AnalyticsPanel({ trades }) {
  const [filter,setFilter]=useState('all');
  const filtered = filter==='sim'?trades.filter(t=>t.isSim):filter==='live'?trades.filter(t=>!t.isSim):trades;
  const stats = calcAnalytics(filtered);

  if(!stats) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:12,color:'#3a4a5a',padding:20}}>
      <div style={{fontSize:32,opacity:0.2}}></div>
      <div style={{fontSize:13,fontWeight:600,color:'#3a4a5a',textAlign:'center'}}>Pas encore de trades fermés</div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflowY:'auto'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}> ANALYTICS</span>
        <div style={{display:'flex',gap:4}}>
          {[['all','Tous'],['live',''],['sim','']].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)}
              style={{padding:'3px 8px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:filter===v?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${filter===v?'#00e5cc44':'#1a2030'}`,color:filter===v?'#00e5cc':'#5a6a80'}}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:'14px',display:'flex',flexDirection:'column',gap:12}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {[['WIN RATE',`${stats.winRate}%`,stats.winRate>=50?'#00e5cc':'#ff6b6b'],['TRADES',`${stats.total}`,'#c0c8d8'],['PNL TOTAL',`${stats.totalPnl>=0?'+':''}${stats.totalPnl.toFixed(2)}$`,stats.totalPnl>=0?'#00e5cc':'#ff6b6b'],['ROI',`${stats.totalPnlPct>=0?'+':''}${stats.totalPnlPct.toFixed(1)}%`,stats.totalPnlPct>=0?'#00e5cc':'#ff6b6b']].map(([label,value,color])=>(
            <div key={label} style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:9,color:'#3a4a5a',marginBottom:4,letterSpacing:'0.8px'}}>{label}</div>
              <div style={{fontSize:18,fontWeight:800,color,fontFamily:'monospace'}}>{value}</div>
            </div>
          ))}
        </div>
        <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:10}}>
            <span style={{color:'#00e5cc'}}> {stats.winners} gagnants</span>
            <span style={{color:'#ff6b6b'}}> {stats.losers} perdants</span>
          </div>
          <div style={{height:6,background:'#1a2030',borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${stats.winRate}%`,background:'linear-gradient(90deg,#00e5cc,#00b8a0)',borderRadius:3}}/>
          </div>
        </div>
        {stats.bestTrade&&(
          <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
            <div style={{fontSize:9,color:'#3a4a5a',marginBottom:8,letterSpacing:'0.8px'}}>MEILLEUR / PIRE TRADE</div>
            <div style={{display:'flex',gap:8}}>
              <div style={{flex:1,background:'rgba(0,229,204,0.05)',border:'1px solid #00e5cc22',borderRadius:6,padding:'8px 10px'}}>
                <div style={{fontSize:9,color:'#3a4a5a',marginBottom:3}}> MEILLEUR</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8'}}>{stats.bestTrade.symbol}</div>
                <div style={{fontSize:13,fontWeight:800,color:'#00e5cc'}}>+{stats.bestTrade.roi?.toFixed(1)}%</div>
              </div>
              <div style={{flex:1,background:'rgba(255,107,107,0.05)',border:'1px solid #ff6b6b22',borderRadius:6,padding:'8px 10px'}}>
                <div style={{fontSize:9,color:'#3a4a5a',marginBottom:3}}> PIRE</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8'}}>{stats.worstTrade.symbol}</div>
                <div style={{fontSize:13,fontWeight:800,color:'#ff6b6b'}}>{stats.worstTrade.roi?.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}
        <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
          <div style={{fontSize:9,color:'#3a4a5a',marginBottom:6,letterSpacing:'0.8px'}}>SIM vs LIVE</div>
          <div style={{display:'flex',gap:8}}>
            <div style={{flex:1,textAlign:'center'}}>
              <div style={{fontSize:9,color:'#7c4dff',marginBottom:4}}> SIM</div>
              <div style={{fontSize:14,fontWeight:800,color:stats.simPnl>=0?'#00e5cc':'#ff6b6b',fontFamily:'monospace'}}>{stats.simPnl>=0?'+':''}{stats.simPnl.toFixed(2)}$</div>
              <div style={{fontSize:9,color:'#3a4a5a'}}>{stats.simCount} trades</div>
            </div>
            <div style={{width:1,background:'#1a2030'}}/>
            <div style={{flex:1,textAlign:'center'}}>
              <div style={{fontSize:9,color:'#00e5cc',marginBottom:4}}> LIVE</div>
              <div style={{fontSize:14,fontWeight:800,color:stats.livePnl>=0?'#00e5cc':'#ff6b6b',fontFamily:'monospace'}}>{stats.livePnl>=0?'+':''}{stats.livePnl.toFixed(2)}$</div>
              <div style={{fontSize:9,color:'#3a4a5a'}}>{stats.liveCount} trades</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaperTradingControl() {
  const [newBal,setNewBal]=useState('');
  const [msg,setMsg]=useState('');
  const currentBal=parseFloat(localStorage.getItem('sim-wallet-balance')||'10');

  const handleReset=()=>{
    const bal=parseFloat(newBal)||10;
    localStorage.setItem('sim-wallet-balance',String(bal));
    setMsg(`Solde sim réinitialisé à ${bal} SOL`);
    setTimeout(()=>setMsg(''),3000);
    setNewBal('');
  };

  const handleClearTrades=()=>{
    if(!window.confirm('Supprimer tout l\'historique sim ? Cette action est irréversible.')) return;
    const trades=JSON.parse(localStorage.getItem('bot-trades')||'[]');
    const liveTrades=trades.filter(t=>!t.isSim);
    localStorage.setItem('bot-trades',JSON.stringify(liveTrades));
    localStorage.removeItem('sim-bot-trades');
    setMsg('Historique sim effacé');
    setTimeout(()=>setMsg(''),3000);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{fontSize:12,color:'#c0c8d8',fontFamily:'monospace',fontWeight:700}}>Solde actuel: {currentBal.toFixed(4)} SOL</div>
      <div style={{display:'flex',gap:6}}>
        <input type="number"placeholder="Nouveau solde (SOL)"value={newBal} onChange={e=>setNewBal(e.target.value)}
          style={{flex:1,background:'#0d1117',border:'1px solid #1a2030',borderRadius:5,color:'#c0c8d8',fontSize:11,padding:'6px 8px',outline:'none'}}/>
        <button onClick={handleReset} style={{padding:'6px 12px',borderRadius:5,background:'rgba(124,77,255,0.2)',border:'1px solid #7c4dff33',color:'#7c4dff',fontSize:10,fontWeight:700,cursor:'pointer'}}>Reset</button>
      </div>
      <button onClick={handleClearTrades} style={{padding:'6px',borderRadius:5,background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer'}}> Effacer historique sim</button>
      {msg&&<div style={{fontSize:10,color:'#00e5cc'}}>{msg}</div>}
    </div>
  );
}function MonitorCenterPanel({ selectedPosition, keypairs, configs }) {
  const [amount,setAmount]=useState('0.1'); const [sellPct,setSellPct]=useState('100%');
  const [slippage,setSlippage]=useState('auto'); const [jitoTip,setJitoTip]=useState('low');
  const [showPopup,setShowPopup]=useState(true);
  const [tradeTab,setTradeTab]=useState('trades');
  const [popupPos,setPopupPos]=useState({x:null,y:10});
  const dragging=useRef(false);
  const dragOffset=useRef({x:0,y:0});
  const popupRef=useRef(null);

  useEffect(()=>{ if(selectedPosition){ setShowPopup(true); setPopupPos({x:null,y:10}); } },[selectedPosition?.mint, selectedPosition?.botWalletId]);

  const onPopupMouseDown=(e)=>{
    if(e.target.tagName==='BUTTON'||e.target.tagName==='SELECT'||e.target.tagName==='INPUT') return;
    dragging.current=true;
    const rect=popupRef.current?.getBoundingClientRect();
    dragOffset.current={x:e.clientX-(rect?.left||0), y:e.clientY-(rect?.top||0)};
    document.body.style.userSelect='none';
  };
  useEffect(()=>{
    const onMove=(e)=>{
      if(!dragging.current||!popupRef.current) return;
      const parent=popupRef.current.parentElement?.getBoundingClientRect();
      if(!parent) return;
      const x=e.clientX-parent.left-dragOffset.current.x;
      const y=e.clientY-parent.top-dragOffset.current.y;
      setPopupPos({x:Math.max(0,Math.min(parent.width-400,x)), y:Math.max(0,Math.min(parent.height-100,y))});
    };
    const onUp=()=>{ dragging.current=false; document.body.style.cursor=''; document.body.style.userSelect=''; };
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  const positionKeypair = selectedPosition?.botWalletId
    ? (botEngine.keypairMap?.[selectedPosition.botWalletId] || null)
    : botEngine.keypair || null;
  const isSim = selectedPosition?.walletIsSim || false;
  const isVPSPosition = !isSim && !positionKeypair && selectedPosition?.botWalletId && selectedPosition.botWalletId !== 'sim';
  const effectiveKeypair = isSim ? 'sim' : positionKeypair;
  const vpsWalletId = isVPSPosition ? selectedPosition.botWalletId : null;
  const canTrade = isSim || positionKeypair || isVPSPosition;
  const {buyLoading,sellLoading,msg,executeBuy,executeSell}=useManualTrade(effectiveKeypair, vpsWalletId);
  const QUICK=['0.01','0.05','0.1','0.25','0.5','1','2','5'];
  const SELL_PCT=['10%','20%','25%','33%','50%','75%','90%','100%'];

  if(!selectedPosition) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:12}}>
      <div style={{fontSize:40,opacity:0.2}}></div>
      <div style={{fontSize:14,fontWeight:600,color:'#3a4a5a'}}>Aucune position sélectionnée</div>
      <div style={{fontSize:12,color:'#2a3a4a'}}>Clique sur une position à gauche pour la gérer</div>
    </div>
  );

  const chartUrl=selectedPosition.mint?`https://birdeye.so/tv-widget/${selectedPosition.mint}?chain=solana&viewMode=pair&autoLoad=true&theme=dark`:null;
  const popupStyle = popupPos.x !== null
    ? {position:'absolute',top:popupPos.y,left:popupPos.x,width:380,zIndex:100}
    : {position:'absolute',top:popupPos.y,left:'50%',transform:'translateX(-50%)',width:380,zIndex:100};

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',position:'relative'}}>
      <div style={{padding:'8px 16px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        <div style={{width:26,height:26,borderRadius:6,background:isSim?'linear-gradient(135deg,#7c4dff22,#7c4dff11)':'linear-gradient(135deg,#00e5cc22,#7c4dff22)',border:`1px solid ${isSim?'#7c4dff33':'#00e5cc33'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:isSim?'#7c4dff':'#00e5cc'}}>{selectedPosition.symbol?.charAt(0)||'?'}</div>
        <div style={{flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{fontSize:13,fontWeight:800,color:'#e0e8f0'}}>{selectedPosition.symbol}</div>
            {selectedPosition.walletName&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:4,background:isSim?'rgba(124,77,255,0.12)':'rgba(0,229,204,0.08)',color:isSim?'#7c4dff':'#00e5cc',border:`1px solid ${isSim?'#7c4dff22':'#00e5cc22'}`,fontWeight:700}}>{isSim?'':''} {selectedPosition.walletName}</span>}
          </div>
          <div style={{fontSize:9,color:'#3a4a5a',fontFamily:'monospace'}}>{selectedPosition.mint?.slice(0,20)}...</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12,fontWeight:800,color:selectedPosition.pnlUsd>=0?'#00e5cc':'#ff6b6b'}}>{selectedPosition.pnlUsd>=0?'+':''}{selectedPosition.pnlUsd?.toFixed(2)}$</span>
          <span style={{fontSize:11,color:selectedPosition.pnlPct>=0?'#00e5cc':'#ff6b6b'}}>{selectedPosition.pnlPct>=0?'+':''}{selectedPosition.pnlPct?.toFixed(1)}%</span>
          <button onClick={()=>setShowPopup(v=>!v)} style={{padding:'5px 12px',borderRadius:5,background:showPopup?'rgba(255,107,107,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${showPopup?'#ff6b6b33':'#00e5cc33'}`,color:showPopup?'#ff6b6b':'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer'}}>
            {showPopup?'Fermer':'Gérer'}
          </button>
        </div>
      </div>
      <div style={{flex:1,position:'relative',minHeight:0,overflow:'hidden'}}>
        {chartUrl&&<iframe key={chartUrl} src={chartUrl} style={{width:'100%',height:'100%',border:'none',display:'block'}} title="Birdeye"/>}
        {showPopup&&(
          <div ref={popupRef} style={{...popupStyle,background:'rgba(11,14,20,0.97)',border:'1px solid rgba(0,229,204,0.3)',borderRadius:10,padding:'0',boxShadow:'0 8px 32px rgba(0,0,0,0.8)',backdropFilter:'blur(8px)'}}>
            <div onMouseDown={onPopupMouseDown}
              style={{padding:'10px 16px',borderBottom:'1px solid #1a2030',cursor:'grab',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'10px 10px 0 0',background:'rgba(0,229,204,0.04)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:10,color:'#3a4a5a'}}></span>
                <span style={{fontSize:13,fontWeight:800,color:'#e0e8f0'}}>{selectedPosition.symbol}</span>
                <span style={{fontSize:10,color:'#5a6a80'}}>{selectedPosition.mint?.slice(0,8)}...</span>
                {selectedPosition.walletName&&<span style={{fontSize:9,padding:'1px 6px',borderRadius:4,background:isSim?'rgba(124,77,255,0.12)':'rgba(0,229,204,0.08)',color:isSim?'#7c4dff':'#00e5cc',border:`1px solid ${isSim?'#7c4dff22':'#00e5cc22'}`,fontWeight:700}}>{isSim?'':''} {selectedPosition.walletName}</span>}
              </div>
              <button onClick={()=>setShowPopup(false)} style={{background:'none',border:'none',color:'#5a6a80',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
            </div>
            <div style={{padding:'14px 16px'}}>
              {isSim&&<div style={{marginBottom:10,padding:'6px 10px',background:'rgba(124,77,255,0.06)',border:'1px solid #7c4dff22',borderRadius:6,fontSize:10,color:'#7c4dff'}}> Position simulée — vente simulée sans vraie tx</div>}
              {msg&&<div style={{marginBottom:10}}><TxMsg msg={msg}/></div>}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
                <div><div style={{fontSize:8,color:'#3a4a5a',marginBottom:3}}>SLIPPAGE</div>
                  <select value={slippage} onChange={e=>setSlippage(e.target.value)} style={{width:'100%',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#8a9ab0',fontSize:10,padding:'4px',outline:'none',cursor:'pointer'}}>
                    <option value="auto">Auto</option><option value="1">1%</option><option value="5">5%</option><option value="10">10%</option>
                  </select>
                </div>
                <div><div style={{fontSize:8,color:'#3a4a5a',marginBottom:3}}>JITO TIP</div>
                  <select value={jitoTip} onChange={e=>setJitoTip(e.target.value)} style={{width:'100%',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#8a9ab0',fontSize:10,padding:'4px',outline:'none',cursor:'pointer'}}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:9,color:'#5a6a80',marginBottom:6,letterSpacing:'0.8px'}}>ACHETER (SOL)</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginBottom:6}}>
                  {QUICK.map(q=>(<button key={q} onClick={()=>setAmount(q)} style={{padding:'5px 0',borderRadius:4,background:amount===q?'rgba(0,229,204,0.2)':'#0d1117',border:`1px solid ${amount===q?'#00e5cc44':'#1a2030'}`,color:amount===q?'#00e5cc':'#5a6a80',fontSize:11,fontWeight:700,cursor:'pointer'}}>{q}</button>))}
                </div>
                <button onClick={()=>executeBuy(selectedPosition.mint,amount,slippage,jitoTip)} disabled={buyLoading||!canTrade}
                  style={{width:'100%',padding:'8px',borderRadius:6,background:buyLoading?'#0d1117':isSim?'linear-gradient(135deg,#7c4dff,#5c2dff)':'linear-gradient(135deg,#00e5cc,#00b8a0)',border:buyLoading?'1px solid #1a2030':'none',color:buyLoading?'#5a6a80':'#fff',fontSize:12,fontWeight:800,cursor:buyLoading||!canTrade?'not-allowed':'pointer',opacity:!canTrade?0.4:1}}>
                  {buyLoading?'⏳ Achat...':isSim?`Sim Acheter ${amount} SOL`:`Acheter ${amount} SOL`}
                </button>
              </div>
              <div>
                <div style={{fontSize:9,color:'#5a6a80',marginBottom:6,letterSpacing:'0.8px'}}>VENDRE</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginBottom:6}}>
                  {SELL_PCT.map(p=>(<button key={p} onClick={()=>setSellPct(p)} style={{padding:'5px 0',borderRadius:4,background:sellPct===p?'rgba(255,107,107,0.2)':'rgba(255,107,107,0.06)',border:`1px solid ${sellPct===p?'#ff6b6b44':'#ff6b6b22'}`,color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer'}}>{p}</button>))}
                </div>
                <button onClick={()=>executeSell(selectedPosition.mint,sellPct,slippage,jitoTip)} disabled={sellLoading||!canTrade}
                  style={{width:'100%',padding:'8px',borderRadius:6,background:sellLoading?'#0d1117':'linear-gradient(135deg,#ff6b6b,#cc4444)',border:sellLoading?'1px solid #1a2030':'none',color:sellLoading?'#5a6a80':'#fff',fontSize:12,fontWeight:800,cursor:sellLoading||!canTrade?'not-allowed':'pointer',opacity:!canTrade?0.4:1}}>
                  {sellLoading?'⏳ Vente...':isSim?`Sim Vendre ${sellPct}`:`Vendre ${sellPct}`}
                </button>
              </div>
              <div style={{marginTop:10,padding:'8px',background:'rgba(0,229,204,0.04)',border:'1px solid #00e5cc11',borderRadius:6,fontSize:10,display:'flex',justifyContent:'space-between'}}>
                <span style={{color:'#5a6a80'}}>{isVPSPosition?'VPS Live':isSim?'Simulation':'Live'}</span>
                <span style={{color:selectedPosition.pnlUsd>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>PNL: {selectedPosition.pnlUsd>=0?'+':''}{(selectedPosition.pnlUsd||0).toFixed(2)}$</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{height:80,borderTop:'1px solid #1a2030',display:'flex',flexDirection:'column',flexShrink:0}}>
        <div style={{display:'flex',borderBottom:'1px solid #1a2030'}}>
          {['trades','holders','top_traders'].map(t=>(
            <button key={t} onClick={()=>setTradeTab(t)} style={{padding:'6px 16px',fontSize:11,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${tradeTab===t?'#00e5cc':'transparent'}`,color:tradeTab===t?'#00e5cc':'#4a5a70'}}>
              {t==='top_traders'?'Top Traders':t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#3a4a5a'}}>
          <span style={{fontSize:11}}>Aucun trade trouvé</span>
        </div>
      </div>
    </div>
  );
}

function SuivreTradingPanel({ selectedConfig, tokenInfo, walletList, balances, keypairs, solPrice, activeCount }) {
  const [amount,setAmount]=useState('0.1');
  const [slippage,setSlippage]=useState('auto');
  const [jitoTip,setJitoTip]=useState('low');

  const configWalletId = selectedConfig?.botWalletId;
  const configWalletInfo = configWalletId ? walletList?.find(w => w.id === configWalletId) : null;
  const configKeypair = configWalletId ? (botEngine.keypairMap?.[configWalletId] || null) : null;
  const isSim = configWalletInfo?.isSim || false;

  const effectiveWallet = configWalletInfo ? {
    address: configWalletInfo.address,
    balance: isSim ? (balances?.['sim'] || 0) : (balances?.[configWalletId] || 0),
    name: configWalletInfo.name,
    id: configWalletInfo.id,
    isSim,
  } : null;

  const effectiveKeypair = isSim ? 'sim': configKeypair;
  const {loading,msg,executeBuy}=useManualTrade(effectiveKeypair);
  const QUICK=['0.01','0.05','0.1','0.25','0.5','1'];

  if(!selectedConfig) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',padding:20,textAlign:'center',gap:12,color:'#3a4a5a'}}>
      <div style={{fontSize:32,opacity:0.2}}></div>
      <div style={{fontSize:12,color:'#3a4a5a'}}>Sélectionne un wallet à gauche pour trader</div>
    </div>
  );

  if(!effectiveWallet) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',padding:20,textAlign:'center',gap:12}}>
      <div style={{fontSize:32}}></div>
      <div style={{fontSize:13,fontWeight:700,color:'#e0e4ec'}}>Wallet non configuré</div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'10px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:26,height:26,borderRadius:6,background:'linear-gradient(135deg,#00e5cc22,#7c4dff22)',border:'1px solid #00e5cc33',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13}}>{isSim?'':''}</div>
        <div style={{flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{fontSize:11,fontWeight:700,color:isSim?'#7c4dff':'#e0e4ec'}}>{effectiveWallet.name}</div>
            {isSim&&<span style={{fontSize:9,padding:'1px 5px',borderRadius:6,background:'rgba(124,77,255,0.15)',color:'#7c4dff',border:'1px solid #7c4dff33',fontWeight:700}}>SIM</span>}
          </div>
          <div style={{fontSize:9,color:'#5a6a80',fontFamily:'monospace'}}>{isSim?'Mode entraînement':effectiveWallet.address.slice(0,6)+'...'+effectiveWallet.address.slice(-4)}</div>
        </div>
        <span style={{fontSize:9,padding:'2px 8px',borderRadius:8,background:activeCount>0?'rgba(0,229,204,0.1)':'#0d1117',color:activeCount>0?'#00e5cc':'#3a4a5a',border:`1px solid ${activeCount>0?'#00e5cc33':'#1a2030'}`,fontWeight:700}}>
          {activeCount>0?'LIVE':'VEILLE'}
        </span>
      </div>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',textAlign:'center'}}>
        <div style={{fontSize:22,fontWeight:800,color:isSim?'#7c4dff':'#e0e4ec',fontFamily:'monospace'}}>{effectiveWallet.balance.toFixed(4)}</div>
        <div style={{fontSize:10,color:'#5a6a80'}}>{isSim?'SOL simulés':'SOL'} · ${(effectiveWallet.balance*(solPrice||150)).toFixed(2)} USD</div>
      </div>
      <div style={{flex:1,padding:'12px 14px',overflowY:'auto'}}>
        {msg&&<div style={{marginBottom:10}}><TxMsg msg={msg}/></div>}
        {tokenInfo?(
          <>
            <div style={{marginBottom:8,padding:'8px 10px',background:'rgba(0,229,204,0.04)',border:'1px solid #00e5cc11',borderRadius:6}}>
              <div style={{fontSize:9,color:'#3a4a5a',marginBottom:4}}>TOKEN SÉLECTIONNÉ</div>
              <div style={{fontSize:12,fontWeight:700,color:'#00e5cc'}}>{tokenInfo.symbol}</div>
              <div style={{fontSize:9,color:'#3a4a5a',fontFamily:'monospace'}}>{tokenInfo.mint?.slice(0,16)}...</div>
            </div>
            <div style={{fontSize:9,color:'#3a4a5a',marginBottom:6,letterSpacing:'0.8px'}}>ACHETER (SOL)</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:4,marginBottom:8}}>
              {QUICK.map(q=>(<button key={q} onClick={()=>setAmount(q)} style={{padding:'6px 0',borderRadius:4,background:amount===q?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${amount===q?'#00e5cc55':'#1a2030'}`,color:amount===q?'#00e5cc':'#5a6a80',fontSize:11,fontWeight:700,cursor:'pointer'}}>{q}</button>))}
            </div>
            <button onClick={()=>executeBuy(tokenInfo.mint,amount,slippage,jitoTip)} disabled={loading}
              style={{width:'100%',padding:'11px',borderRadius:7,background:loading?'#0d1117':isSim?'linear-gradient(135deg,#7c4dff,#5c2dff)':'linear-gradient(135deg,#00e5cc,#00b8a0)',border:loading?'1px solid #1a2030':'none',color:loading?'#5a6a80':'#fff',fontSize:13,fontWeight:900,cursor:loading?'not-allowed':'pointer'}}>
              {loading?'⏳ Envoi...':isSim?`Sim Acheter ${amount} SOL`:`Acheter ${amount} SOL`}
            </button>
          </>
        ):(
          <div style={{textAlign:'center',padding:'30px 0',color:'#3a4a5a',fontSize:11,lineHeight:1.8}}>
            <div style={{fontSize:28,marginBottom:8,opacity:0.2}}></div>
            <div>Clique sur un trade à gauche</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CopytradeTab({ walletAddress, curators=[], onGoToNetwork, botWallet: botWalletProp }) {
  const [configs,setConfigs]=useState(loadConfigs);
  const [selectedId,setSelectedId]=useState(null);
  const [trackConfig,setTrackConfig]=useState(null);
  const [trackToken,setTrackToken]=useState(null);
  const [trackTokenLoading,setTrackTokenLoading]=useState(false);
  const [selectedPosition,setSelectedPosition]=useState(null);
  const [showModal,setShowModal]=useState(false);
  const [editConfig,setEditConfig]=useState(null);
  const [subTab,setSubTab]=useState('configs');
  const [botStarted,setBotStarted]=useState(false);
  const [botStats,setBotStats]=useState(getBotStats());
  const [leftOpen,setLeftOpen]=useState(true);
  const [rightOpen,setRightOpen]=useState(true);
  const [leftWidth,setLeftWidth]=useState(260);
  const [rightWidth,setRightWidth]=useState(280);
  const [solPrice,setSolPrice]=useState(150);
  const [alerts,setAlerts]=useState([]);
  const [allTrades,setAllTrades]=useState([]);

//  Charger les configs depuis Supabase au démarrage
  useEffect(() => {
    loadConfigsFromSupabase().then(supabaseConfigs => {
      if (supabaseConfigs && supabaseConfigs.length > 0) {
        setConfigs(supabaseConfigs);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(supabaseConfigs));
      }
    });
  }, []);

  const draggingLeft=useRef(false);
  const draggingRight=useRef(false);
  const bodyRef=useRef(null);
  const prevTradesRef=useRef([]);

  const { wallet, walletList, balances, activeKeypair, keypairs, activeId } = useBotWallet();
  const activeWallet = botWalletProp || wallet;
  const keypair = botEngine.keypair || (activeId && keypairs ? keypairs[activeId] : null) || activeKeypair || null;
  const activeConfigs=configs.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10);
  const activeCount=activeConfigs.length;
  const showRightPanel = subTab === 'suivre';

useEffect(()=>{
    //  Charger les trades depuis Supabase au démarrage
    const loadTradesFromSupabase = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const { data } = await supabase.from('trades').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false });
        if (data && data.length > 0) {
          const mapped = data.map(t => ({
            id: t.id, symbol: t.symbol, mint: t.mint,
            entreeTs: t.entree_ts, sortieTs: t.sortie_ts,
            achatUsd: t.achat_usd, venteUsd: t.vente_usd,
            pnlUsd: t.pnl_usd, roi: t.roi, resultat: t.resultat,
            isSim: t.is_sim, walletSuivi: t.wallet_suivi,
            txHash: t.tx_hash, tokenAmount: t.token_amount,
            prixEntree: t.prix_entree, botWalletId: null,
          }));
          const local = JSON.parse(localStorage.getItem('bot-trades') || '[]');
          const merged = [...mapped, ...local.filter(l => !mapped.find(m => m.txHash === l.txHash))];
          localStorage.setItem('bot-trades', JSON.stringify(merged));
          setAllTrades(merged);
          prevTradesRef.current = merged;
        }
      } catch(e) { console.warn('Erreur chargement trades:', e.message); }
    };
    loadTradesFromSupabase();

    let tickCount = 0;
    const iv=setInterval(()=>{
      setBotStats(getBotStats());
      tickCount++;
      // Polling Supabase toutes les 10s (every 5 ticks of 2s)
      if (tickCount % 5 === 0) {
        loadTradesFromSupabase();
        return;
      }
      const trades=JSON.parse(localStorage.getItem('bot-trades')||'[]');
      setAllTrades(trades);
      const prev=prevTradesRef.current;
      const newTrades=trades.filter(t=>!prev.find(p=>p.id===t.id));
      if(newTrades.length&&prev.length>0){
        const newAlerts=newTrades.map(t=>({
          type: t.resultat==='En cours'?'buy':'sell',
          symbol: t.symbol, isSim: t.isSim||false,
          detail: t.resultat==='En cours'?`${(t.achatUsd||0).toFixed(2)}$ investi`:`PNL: ${(t.pnlUsd||0)>=0?'+':''}${(t.pnlUsd||0).toFixed(2)}$`,
        }));
        setAlerts(prev=>[...prev,...newAlerts].slice(-5));
      }
      prevTradesRef.current=trades;
    },2000);
    const trades=JSON.parse(localStorage.getItem('bot-trades')||'[]');
    setAllTrades(trades);
    prevTradesRef.current=trades;

    // Abonnement Supabase temps réel — mise à jour instantanée dès qu'un trade est créé/modifié
    let realtimeSub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const userId = session.user.id;
      realtimeSub = supabase.channel('trades-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'trades', filter: `user_id=eq.${userId}` }, () => {
          loadTradesFromSupabase();
        })
        .subscribe();
    });

    return()=>{ clearInterval(iv); if(realtimeSub) supabase.removeChannel(realtimeSub); };
  },[]);

  useEffect(()=>{
    const fetchPrice=async()=>{ const p=await fetchSolPrice(); setSolPrice(p); };
    fetchPrice();
    const iv=setInterval(fetchPrice,30000);
    return()=>clearInterval(iv);
  },[]);

  useEffect(()=>{
    const onMove=(e)=>{
      if(!bodyRef.current) return;
      const rect=bodyRef.current.getBoundingClientRect();
      const x=e.clientX-rect.left;
      if(draggingLeft.current)  setLeftWidth(Math.max(160,Math.min(520,x)));
      if(draggingRight.current) setRightWidth(Math.max(160,Math.min(520,rect.width-x)));
    };
    const onUp=()=>{ draggingLeft.current=false; draggingRight.current=false; document.body.style.cursor=''; document.body.style.userSelect=''; };
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  const startDragLeft=()=>{ draggingLeft.current=true; document.body.style.cursor='col-resize'; document.body.style.userSelect='none'; };
  const startDragRight=()=>{ draggingRight.current=true; document.body.style.cursor='col-resize'; document.body.style.userSelect='none'; };
  const handleTokenFound=(info)=>{ setTrackToken(info); setTrackTokenLoading(false); };
  const dismissAlert=(i)=>setAlerts(prev=>prev.filter((_,idx)=>idx!==i));

  const toggleConfig=(id)=>{
    const config=configs.find(c=>c.id===id);
    const willEnable=!config?.enabled;
    const next=configs.map(c=>c.id===id?{...c,enabled:!c.enabled}:c);
    setConfigs(next); saveConfigs(next);
    if(willEnable&&config?.targetWallet&&config.targetWallet.length>10) {
      const configWallet = walletList.find(w => w.id === config.botWalletId);
      const isSim = configWallet?.isSim || false;
      if(isSim) {
        botEngine.start('', next.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10), (stats)=>{if(stats)setBotStats(stats);}, walletList)
          .then(()=>setBotStarted(true)).catch(console.error);
      } else {
        // Wallet live : le VPS gère l'exécution, pas besoin de mot de passe
        setBotStarted(true);
      }
    }
    else if(botStarted) botEngine.configs=next.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10);
  };

const saveConfig=(data)=>{
    const next=editConfig
      ?configs.map(c=>c.id===editConfig.id?{...c,...data}:c)
      :[...configs,{id:nextConfigId++,name:data.name||'Config',type:'manual',balance:data.amountValue||'0.1',address:data.targetWallet||'N/A',enabled:false,...data}];
    setConfigs(next); saveConfigs(next); setShowModal(false); setEditConfig(null);
  };

  const deleteConfig=(id)=>{
    const next=configs.filter(c=>c.id!==id);
    setConfigs(next); saveConfigs(next);
    if(selectedId===id) setSelectedId(null);
  };

  const selected=configs.find(c=>c.id===selectedId);

  const Divider=({onMouseDown,side})=>(
    <div onMouseDown={onMouseDown}
      style={{width:12,flexShrink:0,background:'#090c12',borderLeft:'1px solid #1a2030',borderRight:'1px solid #1a2030',cursor:'col-resize',display:'flex',alignItems:'center',justifyContent:'center',position:'relative',zIndex:10}}
      onMouseEnter={e=>e.currentTarget.style.background='rgba(0,229,204,0.05)'}
      onMouseLeave={e=>e.currentTarget.style.background='#090c12'}>
      <button onClick={()=>side==='left'?setLeftOpen(v=>!v):setRightOpen(v=>!v)} onMouseDown={e=>e.stopPropagation()}
        style={{width:12,height:36,borderRadius:3,background:'#1a2030',border:'1px solid #2a3040',color:'#5a6a80',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,padding:0}}>
        {side==='left'?(leftOpen?'‹':'›'):(rightOpen?'›':'‹')}
      </button>
    </div>
  );

  return (
    <div className="ct-root">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
      `}</style>

      <InAppAlert alerts={alerts} onDismiss={dismissAlert}/>


      {showModal&&<ConfigModal initial={editConfig} onSave={saveConfig} onClose={()=>{setShowModal(false);setEditConfig(null);}} botWallet={activeWallet} walletList={walletList} balances={balances}/>}

      <div className="ct-stats-bar">
        {(()=>{
          const closedTrades=allTrades.filter(t=>t.resultat&&t.resultat!=='En cours');
          const totalPnl=closedTrades.reduce((s,t)=>s+(t.pnlUsd||0),0);
          const totalInvested=closedTrades.reduce((s,t)=>s+(t.achatUsd||0),0);
          const pnlPct=totalInvested>0?(totalPnl/totalInvested)*100:0;
          const pnl=closedTrades.length>0?totalPnl:botStats.pnlUsd;
          const pct=closedTrades.length>0?pnlPct:botStats.pnlPct;
          return <div className="ct-stat"><span className="ct-stat-lbl">PNL</span><span className={`ct-stat-val ${pnl>=0?'ct-positive':'ct-negative'}`}>{pnl>=0?'+':''}{pnl.toFixed(1)}$ ({pct>=0?'+':''}{pct.toFixed(1)}%)</span></div>;
        })()}
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">OUVERT</span><span className="ct-stat-val">{allTrades.filter(t=>t.resultat==='En cours').length||botStats.openTrades}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">INVESTI</span><span className="ct-stat-val">${(allTrades.filter(t=>t.resultat==='En cours').reduce((s,t)=>s+(t.achatUsd||0),0)||botStats.totalInvested).toFixed(0)}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">CONFIGS ACTIVES</span><span className="ct-stat-val">{activeCount}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat">
          <span style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:7,height:7,borderRadius:'50%',background:activeCount>0?'#00e5cc':'#3a4a5a',animation:activeCount>0?'pulse 2s infinite':undefined}}/>
            <span style={{color:activeCount>0?'#00e5cc':'#5a6a80',fontWeight:700,fontSize:10}}>{activeCount>0?'BOT ACTIF':'BOT EN VEILLE'}</span>
          </span>
        </div>
        <div className="ct-stat-spacer"/>
      </div>

      <div className="ct-subtabs">
        {[['configs','Configs'],['suivre','Suivre'],['positions','Positions'],['analytics','Analytics']].map(([id,label])=>(
          <button key={id} className={`ct-subtab ${subTab===id?'active':''}`} onClick={()=>setSubTab(id)}>{label}</button>
        ))}
      </div>

      <div ref={bodyRef} className="ct-body"style={{display:'flex',flex:1,overflow:'hidden',position:'relative'}}>
        {leftOpen&&subTab!=='analytics'&&(
          <div style={{width:leftWidth,minWidth:160,maxWidth:520,flexShrink:0,display:'flex',flexDirection:'column',background:'#090c12',overflow:'hidden'}}>
            {subTab==='configs'&&<ConfigsPanel configs={configs} selectedId={selectedId} setSelectedId={setSelectedId} toggleConfig={toggleConfig} setEditConfig={setEditConfig} setShowModal={setShowModal} deleteConfig={deleteConfig} onGoToNetwork={onGoToNetwork}/>}
            {subTab==='suivre'&&<SuivreLeftPanel configs={configs} selectedConfig={trackConfig} onSelect={setTrackConfig} onTokenFound={handleTokenFound}/>}
            {subTab==='positions'&&<PositionsPanel botStats={botStats} selectedPosition={selectedPosition} onSelectPosition={setSelectedPosition} allTrades={allTrades} configs={configs}/>}
          </div>
        )}
        {subTab!=='analytics'&&<Divider onMouseDown={leftOpen?startDragLeft:undefined} side="left"/>}
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minWidth:0}}>
          {subTab==='configs'&&(selected?(
            <div className="ct-position-detail">
              <div className="ct-detail-name">{selected.emoji||''} {selected.name}</div>
              <div className="ct-detail-addr">{selected.targetWallet||selected.address}</div>
              <div style={{display:'flex',gap:8,marginTop:12,flexWrap:'wrap',justifyContent:'center'}}>
                {selected.enabled&&selected.targetWallet&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}>🟢 En écoute</div>}
                {selected.amountValue&&<div style={{fontSize:11,color:'#5a6a80',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,padding:'4px 10px'}}> {selected.amountValue} {selected.amountMode==='percentage'?'%':'SOL'}</div>}
                {selected.jitoTip&&<div style={{fontSize:11,color:'#7c4dff',background:'#0d1117',border:'1px solid #7c4dff22',borderRadius:4,padding:'4px 10px'}}> Jito {selected.jitoTip}</div>}
                {selected.copySells&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}> Copie les ventes</div>}
                {selected.stopLossEnabled!==false&&selected.stopLoss&&<div style={{fontSize:11,color:'#ff6b6b',background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',borderRadius:4,padding:'4px 10px'}}> Stop-loss -{selected.stopLoss}%</div>}
                {selected.takeProfitEnabled!==false&&selected.takeProfit&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}>🟢 Take-profit +{selected.takeProfit}%</div>}
                {selected.botWalletId&&walletList.find(w=>w.id===selected.botWalletId)&&(
                  <div style={{fontSize:11,color:'#7c4dff',background:'rgba(124,77,255,0.08)',border:'1px solid #7c4dff22',borderRadius:4,padding:'4px 10px'}}>
                    {walletList.find(w=>w.id===selected.botWalletId)?.isSim?'':''} {walletList.find(w=>w.id===selected.botWalletId)?.name}
                  </div>
                )}
              </div>
            </div>
          ):(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:12}}>
              <div style={{fontSize:40,opacity:0.2}}></div>
              <div style={{fontSize:14,fontWeight:600,color:'#3a4a5a'}}>Sélectionne une config</div>
              <div style={{fontSize:12,color:'#2a3a4a'}}>ou crée-en une nouvelle à gauche</div>
            </div>
          ))}
          {subTab==='suivre'&&<TrackCenterPanel selectedConfig={trackConfig} tokenInfo={trackToken} tokenLoading={trackTokenLoading} keypair={keypair} onTokenFound={handleTokenFound}/>}
          {subTab==='positions'&&<MonitorCenterPanel selectedPosition={selectedPosition} keypairs={keypairs} configs={configs}/>}
          {subTab==='analytics'&&<AnalyticsPanel trades={allTrades}/>}
        </div>
        {showRightPanel&&<Divider onMouseDown={rightOpen?startDragRight:undefined} side="right"/>}
        {showRightPanel&&rightOpen&&(
          <div style={{width:rightWidth,minWidth:160,maxWidth:520,flexShrink:0,display:'flex',flexDirection:'column',background:'#090c12',overflow:'hidden'}}>
            <SuivreTradingPanel selectedConfig={trackConfig} tokenInfo={trackToken} walletList={walletList} balances={balances} keypairs={keypairs} solPrice={solPrice} activeCount={activeCount}/>
          </div>
        )}
      </div>
    </div>
  );
}